import { Dialog, DialogContent } from "@/components/ui/dialog";
import { AssignTarget } from "./AssignTarget";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  orgSlug: string;
  projectSlug: string;
  rowId: string;
  hostname: string;
  current: { type: string | null; branch: string | null };
}

/**
 * Reassign a live hostname's routing target. Wraps `AssignTarget` inside a
 * confirm-style dialog with a from→to header. AssignTarget itself drives
 * PATCH /target; we close the dialog on success via `onAssigned`.
 */
export function ReassignDialog({
  open,
  onOpenChange,
  orgSlug,
  projectSlug,
  rowId,
  hostname,
  current,
}: Props) {
  const currentLabel =
    current.type === "branch" && current.branch
      ? `branch \`${current.branch}\``
      : current.type ?? "—";

  const initial =
    current.type === "production"
      ? { targetType: "production" as const, targetBranch: "" }
      : current.type === "branch"
        ? { targetType: "branch" as const, targetBranch: current.branch ?? "" }
        : { targetType: "production" as const, targetBranch: "" };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <div className="space-y-1">
          <h3 className="text-sm font-semibold">Reassign target</h3>
          <p className="text-xs text-muted-foreground">
            <span className="font-mono">{hostname}</span> currently routes to{" "}
            <span className="font-mono">{currentLabel}</span>. Pick a new target — traffic flips
            within ~10 seconds and no certificate or DNS work is repeated.
          </p>
        </div>
        <AssignTarget
          orgSlug={orgSlug}
          projectSlug={projectSlug}
          rowId={rowId}
          initial={initial}
          submitLabel="Reassign"
          onAssigned={() => onOpenChange(false)}
        />
      </DialogContent>
    </Dialog>
  );
}
